import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronDown, CheckCircle, XCircle, Tool as ToolIcon, BookOpen } from 'react-feather';
import { API_BASE_URL } from '../config/api';
import PageContainer from '../components/PageContainer';
import ActivepiecesWidget from './dashboard/ActivepiecesWidget';
import LogFilterBar, { useLogFilters, matchesDateRange } from '../components/logs/LogFilterBar';
import '../styles/dashboard.css';
import './conversations.css';

// Renders whatever the backend put in tool_data as readable text. Arguments and
// results arrive as objects on some tools and pre-stringified JSON on others,
// so both shapes have to survive without throwing on a circular or non-JSON
// value, a log viewer must never blank out because one row is malformed.
const formatPayload = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// Collapsible tool / knowledge call row.
//
// Shape follows Vercel's AI Elements Tool component: icon, monospace name,
// status pill, chevron; expanding reveals arguments and result. The previous
// version was a flat pill showing only a literal ✓/✗ and the name, so the
// arguments, the returned result, and any error message were fetched but never
// displayed anywhere.
const ToolCallRow = ({ message }) => {
  const [open, setOpen] = useState(false);

  const toolData = message.tool_data || {};
  const succeeded = toolData.status === 'success';
  const isKnowledge = message.role === 'knowledge_call';
  const kind = isKnowledge ? 'Knowledge' : 'Tool';
  const name = toolData.name || message.message || 'unknown';

  const args = formatPayload(toolData.arguments ?? toolData.input ?? toolData.parameters);
  const result = formatPayload(toolData.result ?? toolData.output ?? toolData.response);
  const errorText = formatPayload(toolData.error);
  const hasDetail = Boolean(args || result || errorText);

  return (
    <div className="message tool-call-row">
      <div className="tool-call">
        <button
          type="button"
          className="tool-call-header"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          disabled={!hasDetail}
          title={hasDetail ? undefined : 'No arguments or result were recorded for this call'}
        >
          <span className="tool-call-icon" aria-hidden="true">
            {isKnowledge ? <BookOpen size={14} /> : <ToolIcon size={14} />}
          </span>
          <span className="tool-call-kind">{kind}</span>
          {/* Shown verbatim. Long names ellipsis rather than wrap so the row
              stays one line; title exposes the full value on hover. */}
          <span className="tool-call-name" title={name}>{name}</span>
          <span className="tool-call-spacer" />
          <span
            className={`tool-call-badge ${
              succeeded ? 'tool-call-badge--ok' : 'tool-call-badge--fail'
            }`}
          >
            {succeeded ? <CheckCircle size={11} /> : <XCircle size={11} />}
            {succeeded ? 'Completed' : 'Failed'}
          </span>
          {hasDetail && (
            <span
              className={`tool-call-chevron${open ? ' tool-call-chevron--open' : ''}`}
              aria-hidden="true"
            >
              <ChevronDown size={14} />
            </span>
          )}
        </button>

        {open && hasDetail && (
          <div className="tool-call-body">
            {args && (
              <div>
                <div className="tool-call-field-label">Arguments</div>
                <pre className="tool-call-code">{args}</pre>
              </div>
            )}
            {result && (
              <div>
                <div className="tool-call-field-label">Result</div>
                <pre className="tool-call-code">{result}</pre>
              </div>
            )}
            {errorText && (
              <div className="tool-call-error">
                <div className="tool-call-field-label">Error</div>
                <pre className="tool-call-code">{errorText}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const formatSource = (source) => {
  if (source === 'vapi' || source === 'vapi_call') return 'Phone Call';
  if (source === 'business_chat' || source === 'realtime_voice') return 'Business Assistant';
  return source;
};

const Conversations = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('conversations');

  const { filters, setFilter, resetFilters, activeCount } = useLogFilters();

  const modalRef = useRef(null);
  // The element that had focus before the modal opened, so it can be restored
  // on close, otherwise focus falls back to <body> and keyboard users lose
  // their position in the table.
  const lastFocusedRef = useRef(null);

  const handleLogout = () => {
    console.log('Logout clicked');
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const closeModal = useCallback(() => {
    setSelectedConversation(null);
    setMessages([]);
  }, []);

  // Modal keyboard contract: Escape closes, Tab cycles within the dialog.
  // Without this the overlay was dismissable only by clicking its × button,
  // and focus stayed on the table behind it.
  useEffect(() => {
    if (!selectedConversation) return undefined;

    lastFocusedRef.current = document.activeElement;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, audio[controls], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables || focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Move focus into the dialog so the very next Tab stays inside it.
    modalRef.current?.querySelector('.close-modal-btn')?.focus();
    // The page behind must not scroll while a modal owns the screen.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      // Only restore if the node is still in the document.
      const target = lastFocusedRef.current;
      if (target && document.contains(target)) target.focus();
    };
  }, [selectedConversation, closeModal]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.status === 'success') {
        setConversations(data.conversations);
      } else {
        setError('Failed to fetch conversations');
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(`Error connecting to server: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.status === 'success') {
        setMessages(data.messages);
      } else {
        setError('Failed to fetch messages');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(`Error connecting to server: ${err.message}`);
    }
  };

  const handleConversationClick = async (conversation) => {
    setSelectedConversation(conversation);
    await fetchMessages(conversation.id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatThreadId = (id) => {
    if (!id) return '-';
    if (id.length <= 10) return id;
    return `${id.slice(0, 8)}…`;
  };

  const getMemberLabel = (conversation) => {
    // Only show a member label for internal business-assistant interactions
    const internalSources = new Set(['business_chat', 'realtime_voice']);
    if (!internalSources.has(conversation.source)) return '';

    const p = conversation.participant_user;
    if (!p) return 'Owner';
    return p.contact_name || p.email || `User #${p.id}`;
  };

  // ── Filter option lists, derived from the data actually present ──────────────
  const agentOptions = useMemo(() => {
    const seen = new Map();
    conversations.forEach(c => {
      if (c.agent_id && !seen.has(c.agent_id)) {
        seen.set(c.agent_id, c.agent_name || c.agent_id);
      }
    });
    return [...seen].map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [conversations]);

  const sourceOptions = useMemo(() => {
    const seen = new Set(conversations.map(c => c.source).filter(Boolean));
    return [...seen].map(v => ({ value: v, label: formatSource(v) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [conversations]);

  const statusOptions = useMemo(() => {
    const seen = new Set(conversations.map(c => c.status).filter(Boolean));
    return [...seen].map(v => ({ value: v, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [conversations]);

  // ── Apply search + filters ───────────────────────────────────────────────────
  const filteredConversations = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const minCost = filters.costMin === '' ? null : Number(filters.costMin);
    const maxCost = filters.costMax === '' ? null : Number(filters.costMax);

    return conversations.filter(c => {
      if (filters.agent && c.agent_id !== filters.agent) return false;
      if (filters.source && c.source !== filters.source) return false;
      if (filters.status && c.status !== filters.status) return false;
      if (!matchesDateRange(c.start_time, filters.dateFrom, filters.dateTo)) return false;

      const cost = Number(c.cost);
      if (minCost !== null && !Number.isNaN(minCost)) {
        if (!Number.isFinite(cost) || cost < minCost) return false;
      }
      if (maxCost !== null && !Number.isNaN(maxCost)) {
        if (!Number.isFinite(cost) || cost > maxCost) return false;
      }

      if (!q) return true;
      // Free-text search spans the fields a user can actually see or recall.
      const haystack = [
        c.agent_name,
        formatSource(c.source),
        c.source,
        c.external_id,
        c.customer_phone,
        c.customer_name,
        c.title,
        c.summary,
        c.status,
        getMemberLabel(c),
      ];
      return haystack.some(v => v && String(v).toLowerCase().includes(q));
    });
  }, [conversations, filters]);

  return (
    <PageContainer
      loading={loading}
      loadingMessage="Loading logs..."
      title="Logs"
      subtitle="View conversation and workflow history"
      onLogout={handleLogout}
    >
      {/* ── Tab selector ── */}
      <div className="logs-tabs">
        <button
          className={`logs-tab${activeTab === 'conversations' ? ' logs-tab--active' : ''}`}
          onClick={() => setActiveTab('conversations')}
        >
          Conversation History
        </button>
        <button
          className={`logs-tab${activeTab === 'workflows' ? ' logs-tab--active' : ''}`}
          onClick={() => setActiveTab('workflows')}
        >
          Workflow History
        </button>
      </div>

      {activeTab === 'workflows' ? (
        <ActivepiecesWidget />
      ) : (
        <>
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <LogFilterBar
            filters={filters}
            setFilter={setFilter}
            onReset={resetFilters}
            activeCount={activeCount}
            searchPlaceholder="Search phone, member, thread ID, summary…"
            selects={[
              { key: 'agent',  label: 'Agent',  options: agentOptions,  allLabel: 'All agents'   },
              { key: 'source', label: 'Source', options: sourceOptions, allLabel: 'All sources'  },
              { key: 'status', label: 'Status', options: statusOptions, allLabel: 'All statuses' },
            ]}
            showCost
            resultLabel={`${filteredConversations.length} of ${conversations.length} conversations`}
          />

          <div className="logs-section">
            <h2 className="section-title">History</h2>
            <div className="table-scroll-wrapper">
              <table className="conversation-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Agent</th>
                    <th>Thread ID</th>
                    <th>Member</th>
                    <th>Phone</th>
                    <th>Start Time</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Cost $</th>
                    <th>Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConversations.map((conversation) => (
                    <tr
                      key={conversation.id}
                      onClick={() => handleConversationClick(conversation)}
                      // The row is the control that opens the detail dialog, so
                      // it has to be reachable and activatable by keyboard.
                      tabIndex={0}
                      role="button"
                      aria-label={`View conversation with ${conversation.agent_name || 'agent'}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleConversationClick(conversation);
                        }
                      }}
                      className={`conversation-row ${selectedConversation?.id === conversation.id ? 'selected' : ''}`}
                    >
                      <td data-label="Source">
                        {formatSource(conversation.source)}
                        {conversation.title && (
                          <span className="conversation-title-note">
                            {conversation.title}
                          </span>
                        )}
                        {/* Soft-deleted from the Business Assistant, but the
                            log keeps the full record. */}
                        {conversation.is_deleted && (
                          <span
                            className="conversation-deleted-badge"
                            title="Removed from the Business Assistant. Retained here in the logs."
                          >
                            Removed
                          </span>
                        )}
                      </td>
                      <td data-label="Agent">{conversation.agent_name || '-'}</td>
                      <td data-label="Thread ID" title={conversation.external_id} style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{formatThreadId(conversation.external_id)}</td>
                      <td data-label="Member">{getMemberLabel(conversation)}</td>
                      <td data-label="Phone">{conversation.customer_phone || '-'}</td>
                      <td data-label="Start Time">{formatDate(conversation.start_time)}</td>
                      <td data-label="Duration">{formatDuration(conversation.duration)}</td>
                      <td data-label="Status">{conversation.status}</td>
                      <td data-label="Cost">{conversation.cost}</td>
                      <td data-label="Recording">
                        {conversation.recording_url ? (
                          <audio
                            controls
                            preload="none"
                            src={conversation.recording_url}
                            onClick={(e) => e.stopPropagation()}
                            className="conversation-audio"
                          >
                            Your browser does not support the audio element.
                          </audio>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredConversations.length === 0 && (
                    <tr className="logs-empty-row">
                      <td colSpan="10" data-label="">
                        {conversations.length === 0
                          ? 'No conversations yet.'
                          : 'No conversations match these filters.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>{/* end table-scroll-wrapper */}
          </div>{/* end logs-section */}

          {selectedConversation && (
            <div
              className="conversation-modal-overlay"
              onMouseDown={(e) => {
                // Only a click that both starts and ends on the backdrop
                // dismisses. Using onClick alone closes the dialog when a drag
                // that began inside it (selecting transcript text) happens to
                // release over the backdrop.
                if (e.target === e.currentTarget) closeModal();
              }}
            >
              <div
                className="conversation-modal-content"
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="conversation-modal-title"
              >
                <button
                  type="button"
                  className="close-modal-btn"
                  onClick={closeModal}
                  aria-label="Close conversation details"
                >
                  ×
                </button>

                <div className="messages-section">
                  <h2 className="section-title" id="conversation-modal-title">
                    Conversation Details
                  </h2>

                  {selectedConversation.summary && (
                    <div className="summary-section">
                      <h3 className="summary-title">Summary</h3>
                      <p className="summary-content">
                        {selectedConversation.summary}
                      </p>
                    </div>
                  )}

                  <h3 className="messages-title">Messages</h3>
                  <div className="messages-container">
                    {messages.map((message) => {
                      // Tool / knowledge calls render as a collapsible row
                      // rather than a chat bubble.
                      if (
                        message.role === 'tool_call' ||
                        message.role === 'knowledge_call'
                      ) {
                        return <ToolCallRow key={message.id} message={message} />;
                      }

                      return (
                        <div
                          key={message.id}
                          className={`message ${message.role}`}
                        >
                          <div className="message-timestamp">
                            {message.role} - {formatDate(message.timestamp)}
                          </div>
                          {message.message && <div>{message.message}</div>}
                          {Array.isArray(message.attachments) &&
                            message.attachments.length > 0 && (
                              <div className="message-attachments">
                                {message.attachments.map((att, index) => (
                                  <span
                                    key={index}
                                    className="message-attachment-chip"
                                    title={att.media_type}
                                  >
                                    📎 {att.filename}
                                  </span>
                                ))}
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default Conversations;